import { useEffect, useState } from 'react';
import { Camera, ImageOff, Star, Trash2 } from 'lucide-react';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';

const getAssetLabel = (asset) => asset.caption || asset.assetType || `Asset ${asset.id}`;
const assetTypeOptions = ['exterior', 'interior', 'rack', 'equipment', 'screenshot', 'diagram', 'other'];
const baseVisibilityOptions = ['standard', 'admin'];
const getVisibilityOptions = (isSuperAdmin) => (isSuperAdmin ? baseVisibilityOptions.concat('super_admin') : baseVisibilityOptions);

const ProtectedAssetImage = ({ asset, locationName }) => {
  const authenticatedFetch = useAuthenticatedFetch();
  const [imageState, setImageState] = useState({ status: 'loading', url: null });

  useEffect(() => {
    let isMounted = true;
    let objectUrl = null;

    if (!asset?.id) {
      setImageState({ status: 'empty', url: null });
      return undefined;
    }

    setImageState({ status: 'loading', url: null });

    const loadAsset = async () => {
      try {
        const response = await authenticatedFetch(`/api/locations/assets/${asset.id}`);
        if (!response.ok) throw new Error('Asset request failed.');
        const blob = await response.blob();
        objectUrl = URL.createObjectURL(blob);

        if (isMounted) {
          setImageState({ status: 'loaded', url: objectUrl });
        } else {
          URL.revokeObjectURL(objectUrl);
        }
      } catch (error) {
        if (isMounted) setImageState({ status: 'failed', url: null });
      }
    };

    loadAsset();

    return () => {
      isMounted = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [authenticatedFetch, asset?.id]);

  if (imageState.status === 'loaded' && imageState.url) {
    return (
      <img
        src={imageState.url}
        alt={`${locationName} ${getAssetLabel(asset)}`}
        className="h-full w-full object-cover"
      />
    );
  }

  return (
    <div className="flex h-full flex-col items-center justify-center gap-2 bg-[#111] text-gray-600">
      <ImageOff className="h-7 w-7" aria-hidden="true" />
      <span className="text-xs font-semibold uppercase tracking-widest">Image unavailable</span>
    </div>
  );
};

export default function LocationMediaPanel({ location, assets = [], isAdmin, isSuperAdmin, uploadAsset, deleteAsset, setPrimaryAsset }) {
  const locationName = location?.name || location?.boardName || 'Location';
  const visibilityOptions = getVisibilityOptions(isSuperAdmin);
  const [uploadForm, setUploadForm] = useState({
    file: null,
    assetType: 'exterior',
    visibility: 'standard',
    caption: '',
    isPrimary: false
  });
  const [actionError, setActionError] = useState('');
  const [isUploading, setIsUploading] = useState(false);
  const [pendingPrimaryAssetId, setPendingPrimaryAssetId] = useState(null);

  const updateUploadField = (event) => {
    const { name, value, files, type, checked } = event.target;
    setUploadForm(prev => Object.assign({}, prev, { [name]: files ? files[0] || null : type === 'checkbox' ? checked : value }));
  };

  const handleUpload = async (event) => {
    event.preventDefault();
    if (!uploadAsset || !uploadForm.file) return;
    setActionError('');
    setIsUploading(true);
    try {
      await uploadAsset(uploadForm);
      setUploadForm({ file: null, assetType: 'exterior', visibility: 'standard', caption: '', isPrimary: false });
      event.target.reset();
    } catch (error) {
      setActionError(error.message);
    } finally {
      setIsUploading(false);
    }
  };

  const handleDeleteAsset = async (assetId) => {
    if (!deleteAsset) return;
    setActionError('');
    try {
      await deleteAsset(assetId);
    } catch (error) {
      setActionError(error.message);
    }
  };

  const handleSetPrimaryAsset = async (asset) => {
    if (!setPrimaryAsset || !asset?.id) return;
    setActionError('');
    setPendingPrimaryAssetId(asset.id);
    try {
      await setPrimaryAsset(asset.id);
    } catch (error) {
      setActionError(error.message);
    } finally {
      setPendingPrimaryAssetId(null);
    }
  };

  const canDeleteAsset = (asset) => isAdmin && asset.id && (asset.visibility !== 'super_admin' || isSuperAdmin);
  const isPrimaryAsset = (asset) => Number(location?.primaryPhotoAssetId) === Number(asset?.id);
  const canSetPrimaryPhoto = (asset) => (
    isAdmin && setPrimaryAsset && asset.id && !isPrimaryAsset(asset) && (asset.visibility !== 'super_admin' || isSuperAdmin)
  );

  return (
    <section id="media" className="rounded-lg border border-white/10 bg-[#0b0b0b] p-4">
      <div className="mb-4 flex items-center justify-between gap-3">
        <div>
          <h2 className="text-base font-semibold text-white">Media</h2>
          <p className="text-xs text-gray-500">{isAdmin ? 'Managed location assets' : 'Read-only location assets'}</p>
        </div>
        <span className="rounded-full border border-white/10 px-2 py-1 text-xs font-semibold text-gray-400">
          {assets.length} assets
        </span>
      </div>

      {isAdmin && (
        <form onSubmit={handleUpload} className="mb-4 grid gap-3 rounded-lg border border-white/10 bg-white/[0.03] p-3 sm:grid-cols-2 lg:grid-cols-4">
          <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500 sm:col-span-2">
            File
            <input
              type="file"
              name="file"
              aria-label="Upload location media"
              accept="image/jpeg,image/png,image/webp,image/gif"
              onChange={updateUploadField}
              className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white file:mr-3 file:rounded-md file:border-0 file:bg-white file:px-3 file:py-1 file:text-xs file:font-semibold file:text-black"
            />
          </label>
          <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
            Asset Type
            <select name="assetType" value={uploadForm.assetType} onChange={updateUploadField} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30">
              {assetTypeOptions.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500">
            Visibility
            <select name="visibility" value={uploadForm.visibility} onChange={updateUploadField} className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30">
              {visibilityOptions.map(option => <option key={option} value={option}>{option}</option>)}
            </select>
          </label>
          <label className="grid gap-1 text-xs font-semibold uppercase tracking-widest text-gray-500 sm:col-span-2 lg:col-span-3">
            Caption
            <input
              name="caption"
              value={uploadForm.caption}
              onChange={updateUploadField}
              className="rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-normal text-white outline-none focus:border-white/30"
            />
          </label>
          <label className="flex items-center gap-2 rounded-lg border border-white/10 bg-black px-3 py-2 text-sm font-semibold normal-case tracking-normal text-gray-300 lg:col-span-2">
            <input
              type="checkbox"
              name="isPrimary"
              checked={uploadForm.isPrimary}
              onChange={updateUploadField}
              className="h-4 w-4 accent-white"
            />
            Use as location photo
          </label>
          <div className="flex items-end sm:col-span-2">
            <button
              type="submit"
              disabled={!uploadForm.file || isUploading}
              className="w-full rounded-lg bg-white px-3 py-2 text-xs font-semibold text-black transition-colors hover:bg-gray-200 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {isUploading ? 'Uploading...' : 'Upload'}
            </button>
          </div>
          {actionError && <p className="text-sm text-red-200 sm:col-span-2 lg:col-span-4">{actionError}</p>}
        </form>
      )}

      {assets.length > 0 ? (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {assets.map(asset => (
            <article key={asset.id || `${asset.assetType}-${asset.caption}`} className="overflow-hidden rounded-lg border border-white/10 bg-white/[0.03]">
              <div className="aspect-[4/3] bg-black">
                <ProtectedAssetImage asset={asset} locationName={locationName} />
              </div>
              <div className="space-y-2 p-3">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="min-w-0 text-sm font-semibold text-white">{getAssetLabel(asset)}</h3>
                  <div className="flex shrink-0 items-center gap-2">
                    {(isAdmin || isSuperAdmin) && asset.visibility && (
                      <span className="rounded-full border border-white/10 px-2 py-1 text-[11px] font-semibold text-gray-400">
                        {asset.visibility}
                      </span>
                    )}
                    {canDeleteAsset(asset) && (
                      <button
                        type="button"
                        aria-label="Delete media asset"
                        onClick={() => handleDeleteAsset(asset.id)}
                        className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-white/10 text-gray-400 transition-colors hover:bg-red-500/10 hover:text-red-100"
                      >
                        <Trash2 className="h-4 w-4" aria-hidden="true" />
                      </button>
                    )}
                  </div>
                </div>
                {asset.assetType && <p className="text-xs uppercase tracking-widest text-gray-500">{asset.assetType}</p>}
                {(isPrimaryAsset(asset) || canSetPrimaryPhoto(asset)) && (
                  <div>
                    {isPrimaryAsset(asset) ? (
                      <span className="inline-flex items-center gap-1 rounded-lg border border-emerald-500/20 bg-emerald-500/10 px-2 py-1 text-xs font-semibold text-emerald-100">
                        <Star className="h-3.5 w-3.5" aria-hidden="true" />
                        Location photo
                      </span>
                    ) : (
                      <button
                        type="button"
                        aria-label={`Use ${getAssetLabel(asset)} as location photo`}
                        disabled={pendingPrimaryAssetId === asset.id}
                        onClick={() => handleSetPrimaryAsset(asset)}
                        className="inline-flex items-center gap-1 rounded-lg border border-white/10 px-2 py-1 text-xs font-semibold text-gray-300 transition-colors hover:bg-white/[0.06] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                      >
                        <Star className="h-3.5 w-3.5" aria-hidden="true" />
                        {pendingPrimaryAssetId === asset.id ? 'Setting...' : 'Use as location photo'}
                      </button>
                    )}
                  </div>
                )}
              </div>
            </article>
          ))}
        </div>
      ) : (
        <div className="flex min-h-40 flex-col items-center justify-center gap-3 rounded-lg border border-white/10 bg-white/[0.02] p-6 text-center text-gray-500">
          <Camera className="h-8 w-8 text-gray-600" aria-hidden="true" />
          <p className="text-sm font-semibold text-gray-300">No location media</p>
        </div>
      )}
    </section>
  );
}
